import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Badge } from '@vaiu/ui/badge';
import { Button } from '@vaiu/ui/button';
import { Separator } from '@vaiu/ui/separator';
import { 
  X, 
  Share, 
  Receipt, 
  Copy,
  CheckCircle,
  Calendar,
  Hash,
  User,
  DollarSign
} from 'lucide-react';
import InvoiceTokenBreakdown from './InvoiceTokenBreakdown';

interface TransactionDetailsWithBreakdownProps {
  transactionId: string;
  isOpen: boolean;
  onClose: () => void;
}

const TransactionDetailsWithBreakdown: React.FC<TransactionDetailsWithBreakdownProps> = ({
  transactionId,
  isOpen,
  onClose
}) => {
  // Mock data for demonstration
  const mockTransaction = {
    id: 'LBGYVH',
    type: 'Invoice token',
    amount: 1.00,
    currency: 'USD',
    status: 'Completed',
    created: 'July 16, 2025 at 01:54 PM GMT+2',
    description: 'Coffee and pastry order',
    
    // Invoice-specific data
    invoiceId: 'INV-2025-001',
    invoiceCreatedBy: 'coffee_shop',
    invoiceCreatedAt: '2025-07-16T11:30:00Z',
    
    // Applied tokens data
    appliedTokens: [
      {
        tokenCode: 'ABC123',
        amount: 0.50,
        currency: 'USD',
        appliedAt: '2025-07-16T13:52:00Z',
        appliedBy: 'john_doe',
        tokenCreatedAt: '2025-07-16T13:45:00Z',
        tokenCreatedBy: 'alice_smith',
        status: 'applied' as const,
        transactionId: 'TXN-001'
      },
      {
        tokenCode: 'DEF456',
        amount: 0.30,
        currency: 'USD',
        appliedAt: '2025-07-16T13:53:00Z',
        appliedBy: 'john_doe',
        tokenCreatedAt: '2025-07-16T13:40:00Z',
        tokenCreatedBy: 'bob_wilson',
        status: 'applied' as const,
        transactionId: 'TXN-002'
      },
      {
        tokenCode: 'GHI789',
        amount: 0.20,
        currency: 'USD',
        appliedAt: '2025-07-16T13:54:00Z',
        appliedBy: 'john_doe',
        tokenCreatedAt: '2025-07-16T13:35:00Z',
        tokenCreatedBy: 'charlie_brown',
        status: 'applied' as const,
        transactionId: 'TXN-003'
      }
    ],
    
    totalPaid: 1.00,
    remainingBalance: 0.00,
    paymentStatus: 'completed' as const
  };

  const getCurrencySymbol = (currency: string) => {
    switch (currency) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      default: return currency + ' ';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Receipt className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Transaction Details</h2>
              <Badge variant="secondary" className="mt-1">
                {mockTransaction.type}
              </Badge>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Transaction Overview */}
        <div className="p-6 space-y-6">
          
          {/* Amount Display */}
          <div className="text-center py-4">
            <div className="text-4xl font-bold text-green-600 mb-2">
              +{getCurrencySymbol(mockTransaction.currency)}{mockTransaction.amount.toFixed(2)}
            </div>
          </div>

          {/* Basic Details Grid */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Transaction ID</h3>
              <div className="flex items-center space-x-2">
                <span className="font-mono text-gray-700">{mockTransaction.id}</span>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Created</h3>
              <p className="text-gray-700">{mockTransaction.created}</p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Status</h3>
              <Badge className="bg-green-100 text-green-800">
                <CheckCircle className="w-3 h-3 mr-1" />
                {mockTransaction.status}
              </Badge>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Type</h3>
              <span className="text-orange-600 font-medium">{mockTransaction.type}</span>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Currency</h3>
              <p className="text-gray-700">{mockTransaction.currency}</p>
            </div>
          </div>

          {/* Description */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
            <p className="text-gray-700">{mockTransaction.description}</p>
          </div>

          <Separator />

          {/* Invoice Token Breakdown */}
          <InvoiceTokenBreakdown
            invoiceId={mockTransaction.invoiceId}
            invoiceAmount={mockTransaction.amount}
            invoiceCurrency={mockTransaction.currency}
            invoiceDescription={mockTransaction.description}
            invoiceCreatedBy={mockTransaction.invoiceCreatedBy}
            invoiceCreatedAt={mockTransaction.invoiceCreatedAt}
            appliedTokens={mockTransaction.appliedTokens}
            totalPaid={mockTransaction.totalPaid}
            remainingBalance={mockTransaction.remainingBalance}
            status={mockTransaction.paymentStatus}
          />

          {/* Status Message */}
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div>
                <div className="font-semibold text-green-900">Transaction Completed</div>
                <div className="text-sm text-green-800">
                  This transaction has been successfully processed.
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <Button variant="outline" className="flex-1">
              <Share className="w-4 h-4 mr-2" />
              Share
            </Button>
            <Button variant="outline" className="flex-1">
              <Receipt className="w-4 h-4 mr-2" />
              Receipt
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionDetailsWithBreakdown; 